import moment from "moment";

import { Label } from "semantic-ui-react";

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import auth from "astrid-firebase/src/auth";
import { updateDocument } from "astrid-firestore/src/helpers";

import useDarkMode from "../../../../../hooks/useDarkMode";
import { setActAsUser, useActAsUser } from "../../../../authentication/state/actAsUser";
import { getProfile, useProfile } from "../../../../authentication/state/profile";
import useHasRight from "../../../../authorization/hooks/useHasRight";
import useHasRole from "../../../../authorization/hooks/useHasRole";
import useOrganizationsOnce from "../../../../organizations/hooks/useOrganizationsOnce";
import useGroupBy from "../../../../ui/hooks/useGroupBy";
import { useWhatsNewUnseen } from "../../../../whatsNew/state/whatsNewSeen";

export default function useSecondaryMenuOptions({ organization = {} }) {
	const { t, i18n: i18nInstance } = useTranslation();
	const [isDark, setIsDark] = useDarkMode();
	useEffect(() => {
		moment.locale(i18nInstance.language === "en" ? "en-gb" : i18nInstance.language);
		const p = getProfile();
		if (p && p.languageCode !== i18nInstance.language) {
			updateDocument(p.ref, { languageCode: i18nInstance.language });
		}
	}, [i18nInstance.language]);

	const hasRole = useHasRole();
	const profile = useProfile();
	const navigate = useNavigate();
	const hasRight = useHasRight();
	const actAsUser = useActAsUser();
	const whatsNewUnseen = useWhatsNewUnseen();

	const publisherIds = useMemo(() => Object.keys(profile?.permissions?.publisher || {}), [profile]);
	const publishersById = useGroupBy(useOrganizationsOnce({ ids: publisherIds }), "id");

	return [
		[
			{
				to: `/profile/${profile?.id}`,
				icon: "user outline",
				text: t("profile"),
			},
			{
				to: `/account/${profile?.id}/settings`,
				icon: "setting outline",
				text: t("account", "Account"),
			},
		],
		[
			{
				icon: isDark ? "moon" : "sun",
				text: isDark ? "Dark mode" : "Light mode",
				onClick: () => setIsDark(!isDark),
			},
			{
				icon: "globe",
				text: `${i18nInstance.language === "sv" ? "Svenska" : "English"}`,
				onClick: () => i18nInstance.changeLanguage(i18nInstance.language === "sv" ? "en" : "sv"),
			},
		],

		[
			...Object.keys(profile?.permissions?.producer || {})
				.filter((id) => hasRight("menuOption.ProducerSettings") && id === organization?.id)
				.map((id) => ({
					to: `/settings/${id}`,
					text: t("prodComp"),
				})),

			...Object.keys(profile?.permissions?.publisher || {}).map((id, i) => ({
				to: `/publisher/${id}`,
				text: publishersById?.[id]?.[0]?.name || `${t("profilePublisher")} (${i})`,
			})),
		],
		[
			{
				to: "/reader",
				text: t("narrators", "Narrators"),
				hidden: !hasRight("readers.view"),
			},
			{
				to: "/storytool-archive",
				text: `${t("storytoolArchive", "Storytool archive")}`,
				hidden: !profile?.permissions?.storytoolArchive?.enableArchiveAccess && !hasRole("owner", "admin"),
			},
			{
				to: "/distribution",
				text: t("distribution"),
				hidden: !hasRight("distribution.editDistributors"),
			},
		],
		[
			{
				to: "/studio-redirect",
				icon: "headphones outline",
				text: t("astridStudio"),
				hidden: !hasRight("studioSession.connect"),
			},
			{
				to: "/apps",
				icon: "download outline",
				text: t("apps"),
				hidden: !hasRight("apps.view"),
			},
			{
				to: "/whats-new",
				icon: "gift",
				text: t("whatsNew", "What's new"),
				hidden: !hasRight("whatsNew.view"),
				content: whatsNewUnseen ? (
					<>
						{t("whatsNew", "What's new")}
						<Label color="blue" size="mini" content={t("whatsNewNew", "New")} style={{ marginLeft: 8 }} />
					</>
				) : undefined,
			},
		],
		[
			{
				text: t("profileLogout"),
				hidden: !!actAsUser,
				onClick: () => {
					auth.signOut();
					navigate("/");
				},
			},
			{
				text: t("actAsUserLogout", "Stop acting as user"),
				hidden: !actAsUser,
				onClick: () => {
					setActAsUser();
					navigate(`/admin/users/${actAsUser}`);
				},
			},
		],
	];
}
